import { AnchorButton, Spinner } from '@blueprintjs/core';
import {
  mdiChartBar,
  mdiDomain,
  mdiFolderEdit,
  mdiMap,
  mdiNewspaper,
} from '@mdi/js';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Box } from 'react-layout-components';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'ttag';

import { UserAction } from 'uf-api';
import { AppMode } from 'uf/app/modes';
import {
  getActiveProjectId,
  haveActiveProjectScenarios,
  makeGetActiveScenarioIdForProject,
} from 'uf/app/selectors';
import { assertNever } from 'uf/base/never';
import { getDashboardBase } from 'uf/base/api';
import { setDetailsPaneTabKey, setUIProperty } from 'uf/explore/actions';
import { setDetailsBuildTabVisible } from 'uf/explore/actions/details';
import { TabKey } from 'uf/explore/details';
import {
  getAnalysisUIVisible,
  getBuildUIVisible,
  getLayerListNotVisible,
  getExploreViewVisible,
  getManageViewVisible,
  getReportViewVisible,
  makeGetLayerActiveDetailTabKey,
} from 'uf/explore/selectors/explore';
import { getActiveLayerId } from 'uf/explore/selectors/layers';
import { makeGetActiveViewId } from 'uf/explore/selectors/views';
import { events, logEvent } from 'uf/logging';
import tw from 'uf/tailwindcss-classnames';
import CanPerform from 'uf/ui/base/CanPerform/CanPerform';
import ModeBarItem from 'uf/ui/main/ModeBar/ModeBarItem';
import { useProject, useProjectType } from 'uf/ui/projects/hooks';
import ProjectTypeIcon from 'uf/ui/projects/ProjectTypeIcon/ProjectTypeIcon';
import IconDashboard from './IconDashboard';
import styles from './ModeBar.module.css';
import useUserFlag from 'uf/ui/user/useUserFlag';
import { setExploreNewRoute } from 'uf/explore/actions/routing';
import { parseFullPath } from 'uf/base/dataset';

function logModeSwitch(mode, projectId) {
  logEvent(events.MODE_SWITCHED, { mode, projectId });
}

interface Props {}

const iconStyles = {
  height: '26px',
  width: '26px',
  padding: '3px',
};

export const ModeBar: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const getDetailsTabKey = useSelector(makeGetLayerActiveDetailTabKey);
  const getActiveViewId = useSelector(makeGetActiveViewId);
  const getActiveScenarioId = useSelector(makeGetActiveScenarioIdForProject);

  const projectId = useSelector(getActiveProjectId);
  const scenarioId = useSelector(state =>
    getActiveScenarioId(state, { projectId }),
  );
  const viewId = useSelector(state => getActiveViewId(state, { projectId }));
  const layerId = useSelector(state =>
    getActiveLayerId(state, { projectId, scenarioId, viewId }),
  );
  const activeDetailsTab = useSelector(state =>
    getDetailsTabKey(state, { projectId, layerId }),
  );
  const haveScenarios = useSelector(haveActiveProjectScenarios);
  const exploreViewVisible = useSelector(getExploreViewVisible);
  const layerListNotVisible = useSelector(getLayerListNotVisible);
  const reportViewVisible = useSelector(getReportViewVisible);
  const analysisUIVisible = useSelector(getAnalysisUIVisible);
  const paintUIVisible = useSelector(getBuildUIVisible);
  const manageViewVisible = useSelector(getManageViewVisible);

  const setExploreViewVisible = useCallback(
    (value: boolean) => dispatch(setUIProperty('exploreViewVisible', value)),
    [dispatch],
  );
  const setLayerListNotVisible = useCallback(
    (value: boolean) => dispatch(setUIProperty('layerListNotVisible', value)),
    [dispatch],
  );
  const setReportViewVisible = useCallback(
    () => dispatch(setUIProperty('reportViewVisible', true)),
    [dispatch],
  );
  const setManageViewVisible = useCallback(
    () => dispatch(setUIProperty('manageViewVisible', true)),
    [dispatch],
  );
  const setAnalysisUIVisible = useCallback(
    (value: boolean) => dispatch(setUIProperty('analysisUIVisible', value)),
    [dispatch],
  );
  const setBuildUIVisible = useCallback(
    (value: boolean) => dispatch(setUIProperty('paintUIVisible', value)),
    [dispatch],
  );
  const setBuildTabVisible = useCallback(
    () => dispatch(setDetailsBuildTabVisible(projectId, scenarioId)),
    [dispatch, projectId, scenarioId],
  );
  const setDetailsPaneTab = useCallback(
    (tabKey: TabKey) =>
      dispatch(setDetailsPaneTabKey(projectId, layerId, tabKey)),
    [dispatch, layerId, projectId],
  );

  const project = useProject(projectId);
  const showLocationAnalysis = useUserFlag('location-analysis');
  const { projectType } = useProjectType(project.data?.project_type);

  const onClickProjectType = useCallback(() => {
    const { namespace, key } = parseFullPath(project.key, 'project');
    // if the explore view is not visible, set it to visible
    if (!exploreViewVisible) {
      setExploreViewVisible(true);
      dispatch(setExploreNewRoute(namespace, key, false, layerListNotVisible));
    } else {
      // if the explore view is visible, toggle layerListNotVisible
      setLayerListNotVisible(!layerListNotVisible);
      dispatch(setExploreNewRoute(namespace, key, false, !layerListNotVisible));
    }
  }, [
    project,
    layerListNotVisible,
    exploreViewVisible,
    setLayerListNotVisible,
    setExploreViewVisible,
    dispatch,
  ]);

  const onClickModeItem = useCallback(
    (eventKey: string) => {
      logModeSwitch(eventKey, projectId);
      const dashboardURL = `${getDashboardBase()}/organization${projectId}`;
      const mode = eventKey as AppMode;
      switch (mode) {
        case AppMode.Explore:
          setExploreViewVisible(!exploreViewVisible);
          break;

        case AppMode.Report:
          setReportViewVisible();
          break;

        case AppMode.Paint:
          setBuildUIVisible(!paintUIVisible);
          if (activeDetailsTab === TabKey.BUILD && exploreViewVisible) {
            setDetailsPaneTab(TabKey.STYLE);
          } else {
            setBuildTabVisible();
          }
          break;

        case AppMode.Analyze:
          setAnalysisUIVisible(!analysisUIVisible);
          break;

        case AppMode.Manage:
          setManageViewVisible();
          break;

        case AppMode.Dashboard:
          window.location.href = dashboardURL;
          break;

        default:
          // Unreachable if this switch is exhaustive
          assertNever(mode);
      }
    },
    [
      activeDetailsTab,
      analysisUIVisible,
      exploreViewVisible,
      paintUIVisible,
      projectId,
      setAnalysisUIVisible,
      setBuildTabVisible,
      setBuildUIVisible,
      setDetailsPaneTab,
      setExploreViewVisible,
      setManageViewVisible,
      setReportViewVisible,
    ],
  );

  const buildActive = activeDetailsTab === TabKey.BUILD && exploreViewVisible;
  const hasNoProjects = !(projectType || project.key);

  return (
    <CanPerform verb="scenario.update" resources={projectId}>
      {({ disabled, reason }) => {
        const isHidden =
          disabled && reason === UserAction.ReasonEnum.Permission;
        return (
          <Box
            column
            alignItems="center"
            style={{
              backgroundColor:
                projectType?.style?.background_color ?? '#f4f7fb',
            }}
            className={classNames(styles.modeBar, 'ModeBar')}>
            {projectType || hasNoProjects ? (
              <>
                <AnchorButton
                  className={styles.projectTypeButton}
                  onClick={onClickProjectType}>
                  <div
                    className={styles.modeBarTypeIcon}
                    // 40 suffix on hex code sets opacity to 25%
                    style={{
                      backgroundColor: `${
                        projectType?.style?.color ?? '#6E88A6'
                      }40`,
                    }}>
                    <ProjectTypeIcon
                      source={projectType?.icon_url}
                      label={projectType?.key}
                      size={20}
                    />
                  </div>
                </AnchorButton>
                <ModeBarItem
                  active={exploreViewVisible}
                  className={'Explore'}
                  eventKey={AppMode.Explore}
                  iconPath={mdiMap}
                  onClick={onClickModeItem}
                  tooltipContent={t`Map`}
                />
              </>
            ) : (
              // Prevents disparity in styling when switching to a project of a new type for the first time
              <div className={tw('tw-p-3')}>
                <Spinner size={36} />
              </div>
            )}
            {/* TODO: This will need to be updated once project types other than land use can include a base canvas */}
            {projectType?.key === 'land_use' && !isHidden && (
              <>
                <ModeBarItem
                  active={buildActive}
                  className={'Build'}
                  disabled={!haveScenarios || disabled}
                  disabledTooltipContent={
                    disabled
                      ? t`Build mode is disabled.`
                      : t`Select a Project First`
                  }
                  eventKey={AppMode.Paint}
                  iconPath={mdiDomain}
                  onClick={onClickModeItem}
                  tooltipContent={t`Build`}
                />
                <ModeBarItem
                  disabled={disabled}
                  active={analysisUIVisible}
                  className={'Analyze'}
                  eventKey={AppMode.Analyze}
                  iconPath={mdiChartBar}
                  onClick={onClickModeItem}
                  tooltipContent={t`Analyze`}
                  disabledTooltipContent={t`Analysis is disabled.`}
                />
                <ModeBarItem
                  disabled={disabled}
                  active={reportViewVisible}
                  className={'Report'}
                  eventKey={AppMode.Report}
                  iconPath={mdiNewspaper}
                  onClick={onClickModeItem}
                  tooltipContent={t`Report`}
                  disabledTooltipContent={t`Reporting mode is disabled.`}
                />
                <ModeBarItem
                  disabled={disabled}
                  active={manageViewVisible}
                  className={'Manage'}
                  eventKey={AppMode.Manage}
                  iconPath={mdiFolderEdit}
                  onClick={onClickModeItem}
                  tooltipContent={t`Manage`}
                  disabledTooltipContent={t`Manage mode is disabled.`}
                />
              </>
            )}
            {showLocationAnalysis && (
              <ModeBarItem
                active={false}
                className={'Dashboard'}
                eventKey={AppMode.Dashboard}
                icon={<IconDashboard style={iconStyles} />}
                onClick={onClickModeItem}
                tooltipContent={t`Dashboards`}
              />
            )}
          </Box>
        );
      }}
    </CanPerform>
  );
};

export default ModeBar;
